/**
 * Workspace endpoints — list/get for the 6 surface hubs.
 *
 * Ports v1:
 *   - ``ando-frontend/app/api/unifiedDocuments.ts::listUnifiedDocuments`` (line 58-100)
 *   - ``ando-frontend/app/api/dashboard.ts`` (KPIs / counters)
 *   - ``ando-frontend/app/api/automations.ts`` (the "work" tab)
 *
 * The Phase 3 mocks in ``apps/client/src/routes/_authed/workspace/*`` still
 * render hard-coded fixtures. This Phase 5 deliverable provides the typed
 * API surface so Phase 6 can ``useQuery({ queryFn: listTasks })`` without
 * inventing fetch wrappers per-route.
 */

import { apiClient } from "../ApiClient.js";

// ---------------------------------------------------------------------------
// Documents (workspace → Documenti tab)
// ---------------------------------------------------------------------------

export interface WorkspaceDocument {
  doc_id: string;
  /**
   * BE `content_items.source_type`. The `ai_generated` variant covers files
   * the agent itself materialised in MinIO (`document_create` /
   * `document_fork_edit` tools, May 2026) and lives in `ando-exports`
   * rather than `ando-uploads`.
   */
  /**
   * Canonical DB ``content_source_type`` enum (May 2026). The earlier
   * union shipped ``"upload"`` / ``"chat_attachment"`` as FE aliases,
   * but the BE wire (``unified_content`` view) returns the raw enum
   * — ``"manual_upload"`` + ``"chat"`` — so the alias union silently
   * dropped 14+ docs from the hub on this user's tenant (May 22, 2026
   * incident: "Documenti hub empty"). Single source of truth = DB enum.
   */
  source_type:
    | "drive"
    | "manual_upload"
    | "email_attachment"
    | "chat"
    | "meeting_transcript"
    | "email"
    | "whatsapp"
    | "notion"
    | "ai_generated"
    | "filesystem";
  drive_provider?: "google_drive" | "onedrive" | "sharepoint" | null;
  filename: string;
  title: string;
  mime_type: string | null;
  file_size: number | null;
  indexing_status: "pending" | "processing" | "completed" | "failed";
  chunks_created: number;
  minio_key: string | null;
  /**
   * MinIO bucket name (`ando-uploads` | `ando-exports` | `ando-attachments`
   * | `ando-chat-files`). Present on responses from BE ≥ 1.5.0. Optional
   * for back-compat with older BE deployments — when missing, callers
   * should NOT reconstruct the URL themselves; use `download_url` instead.
   * @deprecated Prefer `download_url`. Kept only for diagnostics / admin UI.
   */
  minio_bucket?: string | null;
  /**
   * Self-contained file download URL served by the BE canonical proxy
   * (`GET /api/v1/documents/{doc_id}/file`). The bucket/key resolution +
   * tenant ACL happen server-side; the client never needs to know the
   * storage layout. Present on responses from BE ≥ 1.5.0. `null` when the
   * row has no MinIO object (e.g. Drive files served by their provider).
   *
   * @example
   *   <a href={doc.download_url ?? "#"} download={doc.filename} />
   */
  download_url?: string | null;
  /**
   * Server-side pin flag (Jun 11, 2026). Backs the "Pinnati" smart-view
   * chip in the Documenti hub — see
   * ``apps/client/src/features/workspace/documents/utils/smartViews.ts``.
   *
   * Toggled via ``POST /api/v1/documents/{doc_id}/pin`` (idempotent),
   * persisted on ``content_items.pinned`` with a partial index on
   * ``(user_id, updated_at DESC) WHERE pinned = TRUE`` for the
   * "give me every pinned row" smart-view lookup. Optional in the
   * client type so older BE deployments that don't yet emit the field
   * silently fall back to the FE's existing ``isPinned`` helper
   * (which treats ``undefined`` as ``false``).
   */
  pinned?: boolean;
  created_at: string | null;
  updated_at: string | null;
  /**
   * Drive folder identity (Jun 23, 2026). Populated for Drive-sourced rows
   * via JOIN ``drive_files_cache → drive_tracked_folders``. ``folder_path``
   * is the materialised "/A/B/C" string and IS the canonical hierarchy
   * signal — the FE rebuilds the tree client-side by splitting on ``/``,
   * so no denormalised ``parent_folder_id`` is exposed (and none exists
   * in the BE schema). ``null`` for non-Drive rows (manual upload, AI
   * generated, email attachments) and for Drive rows whose tracked-
   * folder row has not been ingested yet.
   */
  folder_id?: string | null;
  folder_path?: string | null;
  folder_name?: string | null;
  /**
   * User-defined virtual folder path for NON-Drive sources (Jun 24,
   * 2026). Maintained via :func:`moveDocument`. The unified-list
   * endpoint COALESCEs this into ``folder_path`` so the FE can treat
   * Drive and manual folders identically; this field is mirrored for
   * UIs that need to distinguish "moveable" from "mirrored" rows.
   */
  virtual_folder_path?: string | null;
}

export interface ListDocumentsOptions {
  /**
   * Source type filter — single value OR allow-list. May 2026 the BE
   * extended ``source_filter`` to accept CSV (mirrors ``mime_type``)
   * so the Documenti hub can request "files only" in ONE round-trip
   * instead of paging through 1700+ ``email`` body rows that get
   * dropped by the FE whitelist. Pass an array for the multi-value
   * case; the client joins with commas at serialise time.
   */
  sourceFilter?: WorkspaceDocument["source_type"] | ReadonlyArray<WorkspaceDocument["source_type"]>;
  statusFilter?: WorkspaceDocument["indexing_status"];
  /**
   * Optional comma-separated mime allow-list (May 2026 — BE added the
   * ``mime_type`` query param on ``/documents/unified/list``). The
   * technical hub uses this to fetch ONLY DXF/DWG/STEP/STL rows server-
   * side instead of paginating the whole library + filtering client-side.
   * Whitespace is trimmed BE-side; empty entries are ignored.
   */
  mimeType?: string;
  /**
   * Smart-view shortcut — server-side equivalent of the FE chip strip
   * (Jun 24, 2026). Values: ``tutti`` (no filter), ``drive``, ``ando``,
   * ``pinnati``, ``ricevuti``. Replaces client-side ``matchesSmartView()``
   * so the hub scales past the autofetch window.
   */
  view?: "tutti" | "drive" | "ando" | "pinnati" | "ricevuti";
  /** Time-window filter on ``created_at``. */
  period?: "today" | "7d" | "30d" | "all";
  /**
   * Folder filter — value shape ``<provider>::<path>`` (e.g.
   * ``google_drive::Shared Drives/Ando Central``). Provider can be a
   * real Drive provider OR the synthetic ``manual`` bucket for non-Drive
   * rows (filters ``virtual_folder_path``).
   */
  folder?: string;
  /** Free-text search over filename. Case-insensitive substring. */
  q?: string;
  /** Filter to rows with ``pinned=TRUE``. */
  pinned?: boolean;
  limit?: number;
  offset?: number;
}

export interface ListDocumentsResponse {
  status: string;
  user_id: string;
  total: number;
  limit: number;
  offset: number;
  documents: WorkspaceDocument[];
  error?: string;
}

/**
 * Default MinIO bucket per `WorkspaceDocument.source_type`.
 *
 * @deprecated The client should NOT need to know the bucket. The canonical
 * SOTA path is `doc.download_url` (BE ≥ 1.5.0), served by
 * `GET /api/v1/documents/{doc_id}/file`. This map remains ONLY as a
 * legacy fallback for the transitional period where the client may be
 * pointed at an older BE that hasn't yet been bumped to 1.5.0. Remove
 * once the minimum supported BE version is ≥ 1.5.0 across all envs.
 *
 * If you find yourself adding a new entry here, that's a signal to fix
 * the BE instead (extend `_canonical_download_url` in `documents.py`).
 */
const BUCKET_BY_SOURCE: Record<WorkspaceDocument["source_type"], string> = {
  manual_upload: "ando-uploads",
  email_attachment: "ando-attachments",
  chat: "ando-chat-files",
  // AI-generated files (May 2026): `document_create` / fork-edit tools
  // materialise to ando-exports.
  ai_generated: "ando-exports",
  // Drive files have no minio_key — the presign call is skipped client-side;
  // the bucket here is just a stable placeholder for the type.
  drive: "ando-uploads",
  // Email bodies / WhatsApp / Notion don't live in MinIO at all (they
  // are surfaced as virtual content_items rows for indexing). The
  // legacy fallback URL composer is skipped for these — callers always
  // hit ``doc.download_url`` which the BE only emits when bytes exist.
  email: "ando-attachments",
  whatsapp: "ando-attachments",
  notion: "ando-attachments",
  // Desktop (Tauri) local-folder / NAS files stream to ando-uploads via the
  // presigned PUT in ``/filesystem/desktop/upload-url``.
  filesystem: "ando-uploads",
  // Meeting transcripts (Jun 8, 2026): ingested as ``.txt`` documents via
  // ``ingest_bytes`` → land in the standard uploads bucket.
  meeting_transcript: "ando-uploads",
};

/**
 * Resolve a fetchable URL for a `WorkspaceDocument`.
 *
 * SOTA path: the BE list endpoint (`/documents/unified/list`) already
 * populates `doc.download_url` pointing at the canonical doc-id proxy
 * (`/api/v1/documents/{doc_id}/file`) which derives bucket+key+ACL
 * server-side. The client passes the URL through opaquely — no MinIO
 * layout knowledge leaks into the FE.
 *
 * Legacy fallback: when `download_url` is absent (BE < 1.5.0) we still
 * compose `/documents/minio/file/{bucket}/{key}/url` from the deprecated
 * `BUCKET_BY_SOURCE` map. This branch can be deleted once the minimum
 * BE version is ≥ 1.5.0 in every environment.
 *
 * Returns `null` when the document has no MinIO object at all (e.g. a
 * Drive file served by Google) so the caller can render a "Apri in
 * Drive" CTA instead of a broken viewer.
 */
export async function getDocumentPresignedUrl(
  doc: WorkspaceDocument,
  expiresIn = 3600,
): Promise<string | null> {
  // SOTA path — server-issued canonical URL. No bucket knowledge needed.
  if (doc.download_url) return doc.download_url;

  // Legacy fallback (BE < 1.5.0).
  if (!doc.minio_key) return null;
  const bucket = BUCKET_BY_SOURCE[doc.source_type];
  const path = `/documents/minio/file/${encodeURIComponent(bucket)}/${doc.minio_key
    .split("/")
    .map(encodeURIComponent)
    .join("/")}/url?expires_in=${expiresIn}`;
  try {
    const resp = await apiClient.get<{ status: string; url: string }>(path);
    return resp.url ?? null;
  } catch {
    // Graceful degradation — caller falls back to "Scarica" CTA.
    return null;
  }
}

/** GET ``/documents/unified/list?user_id=...&...`` */
export async function listDocuments(
  userId: string,
  options: ListDocumentsOptions = {},
): Promise<ListDocumentsResponse> {
  const params = new URLSearchParams({ user_id: userId });
  if (options.sourceFilter) {
    // Single value passed as-is; array joined to comma (BE accepts the
    // CSV shape since May 2026 — see ListDocumentsOptions docstring).
    // ``Array.isArray`` doesn't narrow ``ReadonlyArray`` in TS 5.7, so
    // the explicit type guard via ``typeof`` lets the compiler pick
    // the right branch without an ``as`` cast.
    const value: string =
      typeof options.sourceFilter === "string"
        ? options.sourceFilter
        : options.sourceFilter.join(",");
    if (value) params.set("source_filter", value);
  }
  if (options.statusFilter) params.set("status_filter", options.statusFilter);
  if (options.mimeType) params.set("mime_type", options.mimeType);
  if (options.view) params.set("view", options.view);
  if (options.period) params.set("period", options.period);
  if (options.folder) params.set("folder", options.folder);
  if (options.q) params.set("q", options.q);
  if (options.pinned === true) params.set("pinned", "true");
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.offset !== undefined) params.set("offset", String(options.offset));

  return await apiClient.get<ListDocumentsResponse>(
    `/documents/unified/list?${params.toString()}`,
  );
}

/**
 * GET ``/documents/counts`` — per-smart-view badge counts in a single
 * roundtrip. Returns ``{ tutti, ricevuti, drive, ando, pinnati }``.
 * Counts share the same ACL + zombie filter as ``/unified/list``, so
 * the chip badge and the row list can never disagree.
 */
export interface DocumentCounts {
  tutti: number;
  ricevuti: number;
  drive: number;
  ando: number;
  pinnati: number;
}

export interface ListDocumentCountsResponse {
  status: string;
  user_id: string;
  counts: DocumentCounts;
  error?: string;
}

export async function listDocumentCounts(): Promise<ListDocumentCountsResponse> {
  return await apiClient.get<ListDocumentCountsResponse>("/documents/counts");
}

// ---------------------------------------------------------------------------
// Folder tree (workspace → Documenti → left sidebar)
// ---------------------------------------------------------------------------

/**
 * One node returned by ``GET /documents/folders/tree``. The hierarchy is
 * fully encoded in ``folder_path`` (e.g. ``/Clienti/2026/Brescia``) — the
 * FE rebuilds the tree by splitting on ``/`` so siblings group under the
 * longest common ancestor. ``document_count`` is ACL-aware (computed via
 * the same predicate as ``/documents/unified/list``) so the sidebar badge
 * matches the row count the user actually sees after clicking the folder.
 */
export interface FolderTreeNode {
  drive_provider: "google_drive" | "onedrive" | "sharepoint" | string;
  folder_id: string;
  folder_name: string;
  folder_path: string | null;
  document_count: number;
}

export interface ListFolderTreeResponse {
  status: string;
  user_id: string;
  folders: FolderTreeNode[];
  error?: string;
}

/**
 * POST ``/documents/{doc_id}/move`` — assign a virtual folder path to a
 * non-Drive document. Pass ``folder_path: null`` to move back to "Root".
 * Drive rows return 409 — their hierarchy is read-only mirrored.
 */
export async function moveDocument(
  docId: string,
  folder_path: string | null,
): Promise<{ status: string; doc_id: string; folder_path: string | null }> {
  return await apiClient.post<{
    status: string;
    doc_id: string;
    folder_path: string | null;
  }>(
    `/documents/${encodeURIComponent(docId)}/move`,
    { folder_path },
  );
}

/** GET ``/documents/folders/tree`` — flat list of tracked Drive folders. */
export async function listFolderTree(
  options: { driveProvider?: string } = {},
): Promise<ListFolderTreeResponse> {
  const params = new URLSearchParams();
  if (options.driveProvider) params.set("drive_provider", options.driveProvider);
  const qs = params.toString();
  return await apiClient.get<ListFolderTreeResponse>(
    `/documents/folders/tree${qs ? `?${qs}` : ""}`,
  );
}

// ---------------------------------------------------------------------------
// Edit-session — opens a presigned RW window on a single document. The BE
// route is ``POST /api/v1/documents/{doc_id}/edit-session`` (May 2026 SOTA,
// phase A — local-first IndexedDB cache, no server-side lock yet). Phase B
// will reuse the SAME shape and promote ``lock_token`` from ``null`` to a
// real Yjs-backed claim.
//
// Mirror of the BE ``EditSessionResponse`` Pydantic model in
// ``ando-agent/src/api/v1/endpoints/documents.py``.
// ---------------------------------------------------------------------------

/**
 * Stats returned by the BE cascade-delete service after a successful
 * document removal. Mirrors :class:`DeletionResult.to_dict` in
 * ``src/services/cascade_delete_service.py`` — every backend (Weaviate
 * chunks, Neo4j graph nodes, PostgreSQL tracking rows, MinIO objects)
 * reports its own counter so the FE toast can surface the audit-trail
 * summary if needed.
 */
export interface DeleteDocumentResult {
  readonly status?: string;
  readonly source_file?: string;
  /**
   * Per-backend cleanup counters returned by the BE cascade service.
   * ``drive_tracked_files`` (added June 10, 2026) counts the user's
   * Drive sync-tracking rows that the cascade ALSO removed so the
   * document doesn't reappear as "synced" in ``Gestisci file``. Non-
   * Drive sources keep this field at 0.
   */
  readonly deleted?: {
    readonly weaviate_chunks?: number;
    readonly neo4j_nodes?: number;
    readonly postgres?: boolean;
    readonly minio_file?: boolean;
    readonly minio_images?: number;
    readonly enterprise_dim_sources?: number;
    readonly drive_tracked_files?: number;
  };
}

/**
 * DELETE ``/api/v1/documents/{source_file}?cascade=true``. The BE
 * routes through :class:`CascadeDeleteService` (December 2025) which
 * removes the file from Weaviate (all chunks), Neo4j (document
 * nodes), PostgreSQL (tracking rows — soft delete), and MinIO (file +
 * extracted images). Single call, all backends in lockstep.
 *
 * ``sourceFile`` is the BE-keyed filename (``WorkspaceDocument.filename``
 * — NOT the ``doc_id``; the cascade service joins via the filename
 * column). ``cascade=true`` is the default at the BE; we set it
 * explicitly so the FE intent is auditable in the URL.
 */
export async function deleteDocument(
  sourceFile: string,
): Promise<DeleteDocumentResult> {
  const path = `/documents/${encodeURIComponent(sourceFile)}?cascade=true`;
  return apiClient.delete<DeleteDocumentResult>(path);
}

// ---------------------------------------------------------------------------
// Pin / unpin — server-side pin flag for the Documents hub (Jun 11, 2026).
// ---------------------------------------------------------------------------

/**
 * Outcome of ``POST /api/v1/documents/{doc_id}/pin``. Mirror of the BE
 * ``PinDocumentResponse`` Pydantic model in
 * ``ando-agent/src/api/v1/endpoints/documents.py``.
 *
 * ``success === true`` whenever the BE reached the row (idempotent — pinning
 * an already-pinned doc still returns ``true``). ``success === false`` is
 * reserved for transient DB errors swallowed by the BE fail-soft branch; the
 * FE rolls back its optimistic write. ACL miss / not-found surface as a
 * thrown ``ApiError(404)`` instead so the FE can distinguish "your doc, BE
 * blip" from "not your doc".
 *
 * ``pinned`` mirrors the new state so the FE caches don't have to re-read.
 */
export interface PinDocumentResponse {
  readonly success: boolean;
  readonly pinned: boolean;
}

/**
 * Flip the server-side pin flag on a document.
 *
 * Idempotent — calling with the SAME ``pinned`` value as the current row
 * is a no-op success (``rowcount > 0`` still matches). The caller is
 * expected to drive optimistic updates from a TanStack mutation (see
 * ``useTogglePinDocument``) and rely on ``success === false`` to roll back.
 */
export async function pinDocument(
  docId: string,
  pinned: boolean,
): Promise<PinDocumentResponse> {
  return apiClient.post<PinDocumentResponse>(
    `/documents/${encodeURIComponent(docId)}/pin`,
    { pinned },
  );
}

/**
 * EditSession action-state — populated when the document was materialised
 * as an AI **draft** at proposal-time (Phase 1.1 BE work, "Editor-first
 * canvas", May 2026). When ``draft_status === "pending_ai"`` the file
 * lives in the ``drafts/`` bucket and is awaiting a Confirm/Reject decision
 * from the user — the FE renders a slim ``<DraftActionBar>`` above the
 * editor with the rationale + destination picker + CTAs. On Confirm the
 * BE ``/actions/{id}/finalize`` endpoint moves the bytes to the chosen
 * destination and flips this field to ``null`` (or omits it on the next
 * session re-open). On Reject the BE ``/actions/{id}/reject`` deletes the
 * draft object and the editor closes.
 *
 * ``destinations`` mirrors the BE-emitted ``available_destinations`` (the
 * subset of providers the user has actually connected) plus a stable
 * ``download`` / ``minio`` fallback pair so the user always has at least
 * one valid choice.
 *
 * Wire shape mirrors ``EditSessionActionState`` Pydantic model in
 * ``ando-agent/src/api/v1/endpoints/documents.py``.
 */
export interface EditSessionActionState {
  /** Proposal id the BE issued when materialising the draft. */
  readonly proposal_id: string;
  /**
   * Draft lifecycle. ``pending_ai`` = bytes exist in the ``drafts/`` bucket
   * and the user hasn't decided yet. Any other state means the action bar
   * should NOT render (already finalised or rejected).
   */
  readonly draft_status: "pending_ai" | "committed" | "rejected";
  /** Human-readable rationale shown as eyebrow text in the bar. */
  readonly rationale: string;
  /**
   * Identifier of the destination the user (or the BE default) currently
   * has selected. Matches one of ``destinations[].id``. The FE re-emits
   * this on ``finalize`` so the BE knows where to move the bytes.
   */
  readonly selected_destination_id: string;
  /** Save targets the user can pick from in the action bar dropdown. */
  readonly destinations: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly icon?: string | null;
    readonly disabled?: boolean;
  }>;
}

export interface EditSession {
  /** Opaque ``es_<uuid hex>``. The FE uses it ONLY as the IndexedDB key. */
  session_id: string;
  doc_id: string;
  mime_type: string;
  filename: string;
  /** Presigned GET, 3600s. */
  read_url: string;
  /** Presigned PUT, 1800s. Echo ``etag`` as ``If-Match`` when saving. */
  save_url: string;
  save_method: "PUT" | "POST";
  /** MinIO ETag at session-open time. Unquoted; re-quote when sending. */
  etag: string | null;
  /** Reserved for phase B (Yjs collab). Always ``null`` in phase A. */
  lock_token: string | null;
  /** ISO-8601 UTC timestamp at which ``save_url`` expires. */
  expires_at: string;
  /**
   * Conflict-detection baseline (June 2026). ISO-8601 ``modifiedTime`` del
   * file upstream (Drive/OneDrive/SharePoint) al momento dell'apertura
   * della sessione; il BE lo persiste anche in ``content_items.metadata
   * ["drive_modified_time"]``. Al save, ``sync-to-source`` lo confronta
   * col ``modifiedTime`` corrente: se il remoto è più recente → 409
   * + dialog "Sovrascrivere?" lato FE.
   *
   * ``null`` per file MinIO-only (chat upload, manual upload) o quando il
   * provider non risponde (transient → no detection, comportamento legacy).
   */
  remote_modified_baseline?: string | null;
  /**
   * Inline-redline preview (Jun 22, 2026). When a still-pending edit/fork
   * proposal has a native-OOXML-revision redline for this doc, the BE returns
   * its read-only URL here; the editor opens THIS (read-only) instead of
   * ``read_url`` so the redline re-appears on EVERY re-open (chip, library, F5)
   * until accept/reject. ``null``/absent ⇒ open the normal doc.
   */
  redline_url?: string | null;
  /**
   * Proposal id backing ``redline_url`` (Jul 2026) — "Accetta tutte" commits
   * the server-side apply against THIS id directly, no docId matching.
   */
  redline_proposal_id?: string | null;
  /** True when ``redline_url`` is set — open the editor read-only. */
  preview_readonly?: boolean;
  /**
   * Action-state describing whether this document is still an AI draft
   * awaiting Confirm/Reject. ``null`` for every regular (non-AI-drafted)
   * document. See {@link EditSessionActionState}.
   *
   * BE Phase 1.1 wiring — populated by the new ``document_create``
   * materialise-at-proposal-time state machine in
   * ``ando-agent/src/api/v1/endpoints/documents.py``. Until that lands
   * the BE always returns ``null`` here and the action bar never mounts
   * (graceful degradation, no FE crash).
   */
  action_state?: EditSessionActionState | null;
}

// ---------------------------------------------------------------------------
// PDF preview (Office formats → inline PDF). May 2026 P1.
//
// The BE route is ``GET /api/v1/documents/{doc_id}/preview`` and returns
// ``application/pdf`` bytes for DOCX/XLSX/PPTX/ODS/ODT/ODP source docs.
// Cookies-only auth (no token in the URL), so we just hand the path to
// ``<PDFViewerInline>`` and let the browser send credentials. For PDFs the
// BE shortcuts to the canonical ``/file`` proxy — same wire shape, no
// double conversion.
// ---------------------------------------------------------------------------

/**
 * Source MIME types for which the BE can render an inline PDF preview via
 * LibreOffice headless. Mirror of ``_PREVIEW_CONVERTIBLE_MIMES`` in
 * ``ando-agent/src/api/v1/endpoints/documents.py``.
 */
export const PREVIEWABLE_OFFICE_MIMES: ReadonlySet<string> = new Set([
  // PowerPoint / OpenDocument presentation
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
  "application/vnd.oasis.opendocument.presentation",
  // Word / OpenDocument text
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.oasis.opendocument.text",
  // Excel / OpenDocument spreadsheet
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.oasis.opendocument.spreadsheet",
]);

export type PreviewableMime =
  | "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  | "application/vnd.ms-powerpoint"
  | "application/vnd.oasis.opendocument.presentation"
  | "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  | "application/msword"
  | "application/vnd.oasis.opendocument.text"
  | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  | "application/vnd.ms-excel"
  | "application/vnd.oasis.opendocument.spreadsheet";

/**
 * True iff the BE can render an inline PDF preview for this MIME type.
 * Use this to gate the ``<PDFViewerInline>`` branch in the artifact pane.
 */
export function isPreviewableOfficeMime(mime: string | null | undefined): boolean {
  return !!mime && PREVIEWABLE_OFFICE_MIMES.has(mime);
}

/**
 * Returns the canonical preview URL for a document. The browser sends auth
 * cookies on the GET so no token plumbing is needed on the FE side. The BE
 * resolves bucket + key + ACL and either redirects (already-a-PDF source)
 * or renders + caches the LibreOffice conversion.
 *
 * Returned path is relative to the API host — the caller passes it through
 * to ``<PDFViewerInline session={{ read_url, ... }} />``.
 */
export function getDocumentPreviewUrl(docId: string): string {
  return `/api/v1/documents/${encodeURIComponent(docId)}/preview`;
}

/**
 * Returns the ORIGINAL file URL for a document — the raw bytes as stored
 * (e.g. the editable ``.pptx`` / ``.xlsx`` / ``.docx``), NOT the flattened
 * PDF preview from :func:`getDocumentPreviewUrl`. Used by the deck viewer's
 * download button so the user gets a file they can re-open in PowerPoint /
 * Keynote and edit by hand. Mirrors the BE ``_canonical_download_url`` route
 * (``/documents/{doc_id}/file``).
 */
export function getDocumentFileUrl(docId: string): string {
  return `/api/v1/documents/${encodeURIComponent(docId)}/file`;
}

/**
 * ``POST /api/v1/documents/{doc_id}/edit-session`` — opens a single-doc edit
 * window. Caller is expected to:
 *   1. ``fetch(read_url)`` for the current bytes
 *   2. Run the inline editor (PDF / DOCX / XLSX)
 *   3. ``fetch(save_url, { method: save_method, headers: { 'If-Match': etag }, body: blob })``
 *      when the user clicks Save.
 *
 * A 412 Precondition Failed means an out-of-band write rotated the object;
 * the FE should re-open a fresh session and surface a "stale draft" UX.
 */
export async function openEditSession(docId: string): Promise<EditSession> {
  return await apiClient.post<EditSession>(
    `/documents/${encodeURIComponent(docId)}/edit-session`,
    {},
  );
}

/**
 * Mirror of BE ``SyncToSourceResponse`` (see
 * ``src/api/v1/endpoints/documents.py``).
 *
 * ``status`` semantics:
 *   - ``synced``  — bytes pushed upstream to the originating cloud provider.
 *   - ``skipped`` — row has no upstream linkage (MinIO-only, e.g. chat upload)
 *                   or the provider isn't supported yet. Local MinIO save is
 *                   still authoritative.
 *   - ``failed``  — reserved for future explicit failure modes; connector
 *                   errors today surface as HTTP 502 (rejected promise).
 */
export interface SyncToSourceResponse {
  status: "synced" | "skipped" | "failed";
  provider?: string;
  drive_file_id?: string;
  reason?: string;
}

/**
 * Body del 409 Conflict ritornato da ``sync-to-source`` quando un altro
 * utente ha modificato il file remoto dopo l'apertura della nostra
 * edit-session. Specchio di ``HTTPException(409, detail=...)`` lato BE.
 *
 * Il FE intercetta il 409 (``ApiError.status === 409`` +
 * ``isSyncConflict`` type-guard), mostra il dialog "Sovrascrivere?"
 * e — alla conferma — ri-chiama ``syncDocumentToSource(docId, {force:true})``.
 */
export interface SyncConflictPayload {
  readonly status: "conflict";
  readonly remote_modified_iso: string | null;
  readonly remote_modified_by: string | null;
  readonly remote_size_bytes: number | null;
  readonly error?: string | null;
  readonly message: string;
}

/** Type-guard per ``ApiError.data`` quando lo status è 409 da sync-to-source. */
export function isSyncConflictPayload(data: unknown): data is SyncConflictPayload {
  if (!data || typeof data !== "object") return false;
  const obj = data as Record<string, unknown>;
  // FastAPI annida ``detail`` dentro ApiError.data quando si solleva
  // HTTPException(detail={...}); proviamo entrambe le shape.
  const candidate =
    obj.status === "conflict"
      ? obj
      : obj.detail && typeof obj.detail === "object"
        ? (obj.detail as Record<string, unknown>)
        : null;
  return !!candidate && candidate.status === "conflict";
}

/**
 * Estrae il payload conflict da ``ApiError.data`` (gestisce sia la shape
 * piatta che ``{detail: {...}}`` di FastAPI HTTPException). Ritorna
 * ``null`` se il body non ha la shape attesa.
 */
export function extractSyncConflictPayload(data: unknown): SyncConflictPayload | null {
  if (!data || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;
  if (obj.status === "conflict") {
    return obj as unknown as SyncConflictPayload;
  }
  if (obj.detail && typeof obj.detail === "object") {
    const inner = obj.detail as Record<string, unknown>;
    if (inner.status === "conflict") {
      return inner as unknown as SyncConflictPayload;
    }
  }
  return null;
}

export interface SyncDocumentToSourceOptions {
  /**
   * Bypassa il conflict-check lato BE. Settare SOLO dopo che l'utente ha
   * cliccato "Sovrascrivi" nel dialog di conflitto (vedi
   * ``SyncConflictPayload``). Default ``false`` — il BE rileva e ritorna 409.
   */
  force?: boolean;
}

/**
 * ``POST /api/v1/documents/{doc_id}/sync-to-source`` — push the MinIO copy of
 * ``docId`` back to its originating cloud provider (Google Drive / OneDrive /
 * SharePoint) so the round-trip "AI modifies → MinIO → Drive" closes.
 *
 * Safe to call unconditionally after any successful MinIO save — the BE
 * returns ``{status: "skipped"}`` for MinIO-only rows. No client-side
 * type-sniff needed (DRY: ``content_items.source_provider`` is the single
 * source of truth on the BE).
 *
 * Conflict (June 2026): se un altro utente ha modificato il file remoto
 * dopo l'apertura della edit-session, il BE solleva ``HTTP 409`` con
 * :class:`SyncConflictPayload`. Il caller DEVE intercettare via
 * ``isSyncConflictPayload`` / ``extractSyncConflictPayload``, mostrare il
 * dialog di conferma, e — se l'utente accetta l'overwrite — re-chiamare
 * con ``{ force: true }``.
 */
export async function syncDocumentToSource(
  docId: string,
  options: SyncDocumentToSourceOptions = {},
): Promise<SyncToSourceResponse> {
  const qs = options.force ? "?force=true" : "";
  return await apiClient.post<SyncToSourceResponse>(
    `/documents/${encodeURIComponent(docId)}/sync-to-source${qs}`,
    {},
  );
}

// ---------------------------------------------------------------------------
// Calendar — see ``./calendar.ts``. The earlier aspirational shape in this
// file (``start_iso``/``end_iso``/``source``) didn't match the BE wire shape;
// the canonical ``CalendarEvent`` + ``getCalendarEvents`` now live in
// ``./calendar.ts`` and use the v1-verified field names
// (``start_time``/``end_time``/``provider``).
// ---------------------------------------------------------------------------
// Tasks / Work (workspace → Work tab)
// ---------------------------------------------------------------------------

export interface WorkspaceTask {
  task_id: string;
  title: string;
  status: "todo" | "in_progress" | "blocked" | "done";
  due_date?: string;
  assignee?: { user_id: string; display_name: string };
  source?: "automation" | "manual" | "chat";
}

export interface ListTasksOptions {
  status?: WorkspaceTask["status"];
  limit?: number;
}

/** GET ``/tasks?user_id=...&status=...`` */
export async function listTasks(
  userId: string,
  options: ListTasksOptions = {},
): Promise<{ tasks: WorkspaceTask[] }> {
  const params = new URLSearchParams({ user_id: userId });
  if (options.status) params.set("status", options.status);
  if (options.limit !== undefined) params.set("limit", String(options.limit));

  return await apiClient.get<{ tasks: WorkspaceTask[] }>(`/tasks?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Finance — see ``./finance.ts``. The earlier placeholder shape in this file
// (``{ kpis: ... }``) didn't match the real BE wire; the canonical
// ``FinanceOverview`` + ``getFinanceOverview`` now live in ``./finance.ts``
// (composite cash-flow + DSCR + burn-rate from the BE
// ``/finance/overview`` endpoint shipped in cdb0999).
// ---------------------------------------------------------------------------
